<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import type { CollapsibleContentProps } from 'reka-ui'
import { CollapsibleContent, injectCollapsibleRootContext, useForwardProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * The part of a `Collapsible` that only exists while it is open: nothing inside it is drawn while closed.
 *
 * One change from the upstream shadcn-vue recipe: what the panel holds leaves in the same render as the state that
 * closed it. Reka's panel waits a tick for its presence check (which exists to let a closing animation play) before
 * it marks itself hidden. Nothing in this kit animates a collapsible, so there is nothing to wait for, and in that
 * tick a trigger already reading `aria-expanded="false"` would sit above content still on screen. With `forceMount`,
 * or `unmountOnHide: false` on the root (Reka's `hidden="until-found"` for find-in-page), the content stays mounted
 * as Reka keeps it.
 */
const props = defineProps<CollapsibleContentProps & { class?: HTMLAttributes['class'] }>()

const delegatedProps = reactiveOmit(props, 'class')
const forwardedProps = useForwardProps(delegatedProps)

const root = injectCollapsibleRootContext()
const rendered = computed(() => props.forceMount || !root.unmountOnHide.value || root.open.value)
</script>

<template>
  <CollapsibleContent data-slot="collapsible-content" v-bind="forwardedProps" :class="cn(props.class)">
    <slot v-if="rendered" />
  </CollapsibleContent>
</template>
